import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Button,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  CircularProgress,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import SearchBar from "../../components/SearchBar";
import CommonBreadcrumb from "../../components/CommonBreadcrumb";
import SwitchToggle from "../../components/SwitchToggle";
import AddCategory from "../../Modals/addCategory";
import DeleteConfirmModal from "../../Modals/DeleteConfirmModal";
import { usePermission } from "../../hooks/usePermission";
import Can from "../../components/Can";
import {
  useGetCategoriesQuery,
  useDeleteCategoryMutation,
  useUpdateCategoryMutation
} from "../../service/features/api";
import Pagination from "../../components/Panigation";
import moment from "moment";
import { toast } from "react-toastify";
import { Box, Tab, Tabs } from "@mui/material";
import { VEHICLE_TYPE } from "../../constants/enum";
import { getVehicleTypeLabel } from "../../utils/vehicle";

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function CustomTabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`simple-tabpanel-${index}`}
      aria-labelledby={`simple-tab-${index}`}
      {...other}
    >
      {value === index && <>{children}</>}
    </div>
  );
}

function a11yProps(index: number) {
  return {
    id: `simple-tab-${index}`,
    "aria-controls": `simple-tabpanel-${index}`,
  };
}

const typeMapping = [
  VEHICLE_TYPE.CAR,
  VEHICLE_TYPE.BIKE,
  VEHICLE_TYPE.BICYCLE,
  VEHICLE_TYPE.CARAVAN,
  VEHICLE_TYPE.TRUCK,
  VEHICLE_TYPE.HEAVY_VEHICLE
];
const tabTypes = typeMapping.map(type => getVehicleTypeLabel(type));

const ManageCategories = () => {
  const navigate = useNavigate();
  const { hasPermission } = usePermission();
  const canEdit = hasPermission("Categories Management", "isEdit");

  const [searchTerm, setSearchTerm] = useState("");
  const [openModal, setOpenModal] = useState(false);
  const [openDeleteModal, setOpenDeleteModal] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState<any>(null);
  const [page, setPage] = useState(1);
  const [valueTabs, setValueTabs] = useState(0);

  const { data: categoriesData, isFetching } = useGetCategoriesQuery({
    search: searchTerm,
    page: page,
    limit: 10,
    type: typeMapping[valueTabs]
  });

  const handleChangeTabs = (event: React.SyntheticEvent, newValue: number) => {
    setValueTabs(newValue);
    setPage(1);
  };

  const [deleteCategory, { isLoading: isDeleting }] = useDeleteCategoryMutation();
  const [updateCategory] = useUpdateCategoryMutation();

  const handleToggle = async (category: any, field: string, value: boolean) => {
    const vType = category.type;
    const payload: any = {
      id: category._id,
      name: category.name,
      isPopular: category.isPopular === "true" || category.isPopular === true,
      isActive: category.isActive,
      order: category.order || 1,
      type: Array.isArray(vType) ? vType : (vType ? [vType] : []),
      [field]: value // Override the changed field
    };

    if (category.icon) payload.icon = category.icon;
    if (category.image) payload.image = category.image;

    try {
      await updateCategory(payload).unwrap();
      toast.success("Status updated successfully");
    } catch (err: any) {
      toast.error(err?.data?.message || "Update failed");
    }
  };

  const categories = categoriesData?.data?.data || [];
  const paginator = categoriesData?.data?.paginator;

  const handleEdit = (category: any) => {
    setSelectedCategory(category);
    setOpenModal(true);
  };

  const handleDeleteClick = (category: any) => {
    setSelectedCategory(category);
    setOpenDeleteModal(true);
  };

  const handleConfirmDelete = async () => {
    if (!selectedCategory?._id) return;
    try {
      const res = await deleteCategory(selectedCategory._id).unwrap();
      toast.success(res.message || "Category deleted successfully");
      setOpenDeleteModal(false);
      // If last item on page deleted, go back a page
      if (categories.length === 1 && page > 1) {
        setPage(page - 1);
      }
    } catch (err: any) {
      toast.error(err?.data?.message || "Failed to delete category");
    }
  };

  return (
    <>
      {/* PAGE TITLE */}
      <CommonBreadcrumb
        title="إدارة الفئات / Categories Management"
        breadcrumbItems={[
          { label: "لوحة المعلومات / Dashboard", onClick: () => navigate("/dashboard") },
          { label: "إدارة الفئات / Categories Management" },
        ]}
      />

      <div className="table_header">
        <div className="left_s">
          <Box className="custom_tabs1" sx={{ width: "100%" }}>
            <Tabs
              value={valueTabs}
              onChange={handleChangeTabs}
              className="custom_tabs_links"
            >
              {tabTypes.map((label, index) => (
                <Tab key={label} label={label} {...a11yProps(index)} />
              ))}
            </Tabs>
          </Box>
        </div>
      </div>

      <div className="cards">
        {/* TABLE HEADER */}
        <div className="table_header">
          <div className="left_s">
            <SearchBar
              searchTerm={searchTerm}
              setDebouncedSearchTerm={(val) => {
                setSearchTerm(val);
                setPage(1);
              }}
              placeholder="بحث... / Search..."
            />
          </div>
          <div className="right_s">
            <Can module="Categories Management" action="isAdd" isDisabledMode={true}>
              <Button
                className="btn btn_primary"
                onClick={() => {
                  setSelectedCategory(null);
                  setOpenModal(true);
                }}
              >
                <AddIcon /> إضافة فئة / Add Category
              </Button>
            </Can>
          </div>
        </div>

        {tabTypes.map((type, tabIndex) => (
          <CustomTabPanel key={type} value={valueTabs} index={tabIndex}>
            {/* TABLE */}
            <TableContainer className="table_container">
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>م / Sr. No</TableCell>
                <TableCell>تفاصيل الفئة / Category Details</TableCell>
                <TableCell>الفئات الفرعية / Sub-Categories</TableCell>
                <TableCell>تاريخ الإنشاء / Created Date</TableCell>
                <TableCell>تعطيل/تمكين / Disable/Enable</TableCell>
                <TableCell>شائع / Popular</TableCell>
                <TableCell>إجراءات / Actions</TableCell>
              </TableRow>
            </TableHead>

            <TableBody>
              {isFetching ? (
                <TableRow>
                  <TableCell colSpan={7} align="center">
                    <CircularProgress />
                  </TableCell>
                </TableRow>
              ) : categories.map((category: any, index: number) => (
                  <TableRow hover key={category._id}>
                  {/* SR NO */}
                  <TableCell>{(page - 1) * 10 + index + 1}</TableCell>


                  {/* CATEGORY NAME */}
                  <TableCell>
                    <div className="user_block">
                      <figure className="no_radius">
                        <img
                          src={category.image || category.icon || "/static/images/placeholder.png"}
                          alt={category.name}
                          onError={(e: any) => { e.target.src = "/static/images/placeholder.png" }}
                          style={{ objectFit: "contain"}}
                        />
                      </figure>
                      <p>
                        <b>{category.name}</b>
                      </p>
                    </div>
                  </TableCell>

                  {/* SUB CATEGORIES */}
                  <TableCell>
                    <Button
                      className="btn btn_primary sm"
                      onClick={() =>
                        navigate("/manage-categories/sub-categories", { state: { categoryId: category._id } })
                      }
                    >
                      عرض / View ({category.subCategories?.length || 0})
                    </Button>
                  </TableCell>

                  {/* CREATED DATE */}
                  <TableCell>{moment(category.createdAt).format("DD-MMM-YYYY")}</TableCell>

                  {/* STATUS */}
                  <TableCell>
                    {canEdit ? (
                      <SwitchToggle
                        checked={category.isActive}
                        onChange={(e) => handleToggle(category, "isActive", e.target.checked)}
                      />
                    ) : (
                      <span className={`status_btn ${category.isActive ? "c_dark_info" : "c_dark_warning"}`}>
                        {category.isActive ? "Enabled" : "Disabled"}
                      </span>
                    )}
                  </TableCell>

                  <TableCell>
                    {canEdit ? (
                      <SwitchToggle
                        checked={category.isPopular === "true" || category.isPopular === true}
                        onChange={(e) => handleToggle(category, "isPopular", e.target.checked)}
                      />
                    ) : (
                      <span className={`status_btn ${(category.isPopular === "true" || category.isPopular === true) ? "c_dark_info" : "c_dark_warning"}`}>
                        {(category.isPopular === "true" || category.isPopular === true) ? "Popular" : "Normal"}
                      </span>
                    )}
                  </TableCell>

                  {/* ACTIONS */}
                  < TableCell >
                    <div className="table_actions v2">
                      <Can module="Categories Management" action="isEdit" isDisabledMode={true}>
                        <Tooltip title="Edit" arrow>
                          <IconButton onClick={() => handleEdit(category)}>
                            <img
                              src="/static/images/edit2_icon.svg"
                              alt="Edit"
                            />
                          </IconButton>
                        </Tooltip>
                      </Can>
                      <Can module="Categories Management" action="isDelete" isDisabledMode={true}>
                        <Tooltip title="Delete" arrow>
                          <IconButton onClick={() => handleDeleteClick(category)}>
                            <img
                              src="/static/images/trash2_icon.svg"
                              alt="Delete"
                            />
                          </IconButton>
                        </Tooltip>
                      </Can>
                    </div>
                  </TableCell>
                </TableRow>
              ))}
              {!isFetching && categories.length === 0 && (
                <TableRow>
                  <TableCell colSpan={7} align="center">
                    لا توجد فئات / No Categories Found
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer >

        {paginator && paginator.pageCount > 1 && (
          <Pagination
            module={categories}
            page={page}
            onPageChange={(p) => setPage(p)}
            totalPages={paginator.pageCount}
            totalCount={paginator.itemCount}
            setPage={setPage}
          />
        )
        }
          </CustomTabPanel>
        ))}
      </div >

      {/* ADD / EDIT MODAL */}
      < AddCategory
        open={openModal}
        onClose={() => setOpenModal(false)}
        setOpen={setOpenModal}
        categoryData={selectedCategory}
        defaultType={typeMapping[valueTabs]}
      />

      {/* DELETE CONFIRM MODAL */}
      < DeleteConfirmModal
        open={openDeleteModal}
        onClose={() => setOpenDeleteModal(false)}
        onConfirm={handleConfirmDelete}
        isLoading={isDeleting}
        message={`Are you sure you want to delete the category "${selectedCategory?.name}"${selectedCategory?.name_ar ? ` / "${selectedCategory?.name_ar}"` : ""}?`}
      />
    </>
  );
};

export default ManageCategories;


